import React from "react";
import toRupiah from "../../utils/toRupiah";
import toRibuan from "../../utils/toRibuan";
import StarIcon from "@mui/icons-material/Star";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useTransaksi } from "../../context/transaksiContext";
import { useDetailBarangModal } from "../../context/detailBarangModalContext";
import {
    Avatar,
    Box,
    Card,
    Button,
    Typography,
    Stack,
    Grid,
    Link,
    useMediaQuery,
    Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "./CardBarang.css";
import avatarAlt from "../../utils/avatarAlt";
import {
    ContentGetItemResponse,
    ParamsGetWarehouse,
} from "../../constants/types";
import truncate from "../../utils/truncate";
import useWarehouse from "../../services/queries/useWarehouse";
import useUser from "../../services/queries/useUser";
import { getLocalWarehouseId } from "../../utils/handleChangeWarehouseId";

interface ICardBarangProps {
    data: ContentGetItemResponse;
}

const CardBarang = ({ data }: ICardBarangProps) => {
    const {
        id,
        namaBarang,
        kategori,
        deskripsi,
        harga,
        multipleGambar,
        sku,
        stok: jumlahStok,
        stokPusat,
        detailBundling,
        favorite,
        hpp,
        hargaMultiple,
    } = data;
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { addToCart, cart } = useTransaksi();
    const { openModalDetailBarang } = useDetailBarangModal();

    const { data: user } = useUser();

    const initialParamsWarehouse = {
        size: 500,
        page: 1,
        search: "",
    };

    const [paramsWarehouse, setParamsWarehouse] =
        React.useState<ParamsGetWarehouse>(initialParamsWarehouse);
    const { data: warehouse } = useWarehouse(paramsWarehouse);

    const warehouseId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalWarehouseId(user?.userId);
        }
        return null;
    }, [user]);

    const warehouseData = React.useMemo(() => {
        if (warehouseId) {
            return warehouse?.content.find((li) => li.id === warehouseId);
        }
        return warehouse?.content[0];
    }, [warehouseId, warehouse]);

    React.useEffect(() => {
        if (user && user?.warehouseId?.length > 0) {
            setParamsWarehouse((prev) => ({
                ...prev,
                userId: user.warehouseId[0],
            }));
        }
    }, [user]);

    return (
        <React.Fragment>
            <Card
                variant="outlined"
                sx={{
                    marginBottom: 1,
                    padding: 2,
                }}
            >
                <Box
                    display="grid"
                    gridTemplateColumns="1fr auto"
                    rowGap={isPhoneScreen ? 1 : 3}
                    sx={{
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: {
                            xs: "space-between",
                            sm: "space-between",
                        },
                    }}
                >
                    <Stack
                        direction={{ xs: "row-reverse", sm: "row" }}
                        sx={{
                            whiteSpace: "nowrap",
                            // overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        spacing={2}
                    >
                        <Box sx={{ marginBottom: favorite > 0 ? -5 : 0 }}>
                            <Link
                                onClick={() => openModalDetailBarang(data)}
                                color="#000000"
                                underline="none"
                                sx={{ cursor: "pointer" }}
                            >
                                <Avatar
                                    sx={{
                                        backgroundColor: "primary.main",
                                        width: {
                                            xs: 60,
                                            sm: 60,
                                            md: 70,
                                            lg: 60,
                                            xl: 80,
                                        },
                                        height: {
                                            xs: 60,
                                            sm: 60,
                                            md: 90,
                                            lg: 70,
                                            xl: 90,
                                        },
                                    }}
                                    variant="rounded"
                                    alt={namaBarang}
                                    src={
                                        multipleGambar &&
                                        multipleGambar.length > 0
                                            ? multipleGambar[0].gambar
                                            : undefined
                                    }
                                >
                                    {avatarAlt(namaBarang)}
                                </Avatar>
                            </Link>
                            {favorite > 0 && (
                                <StarIcon
                                    sx={{
                                        width: isSmallScreen ? 32 : undefined,
                                        height: isSmallScreen ? 32 : undefined,
                                        position: "relative",
                                        zIndex: 2,
                                        bottom: {
                                            xl: 105,
                                            lg: 86,
                                            sm: 70,
                                            xs: 70,
                                        },
                                        left: "70%",
                                        border: 1,
                                        borderColor: "#e7e5e4",
                                        borderRadius: "50%",
                                        padding: isPhoneScreen ? 0.5 : 0.25,
                                        color: "#fbbf24",
                                        bgcolor: "#FFFFFF",
                                    }}
                                    fontSize="large"
                                />
                            )}
                        </Box>
                        <Grid container>
                            <Grid item>
                                <Link
                                    onClick={() => openModalDetailBarang(data)}
                                    color="#000000"
                                    underline="none"
                                    sx={{ cursor: "pointer" }}
                                >
                                    <Tooltip title={namaBarang}>
                                        <Typography
                                            fontSize={isPhoneScreen ? 14 : 16}
                                            fontWeight="bold"
                                        >
                                            {truncate(namaBarang, 20)}
                                        </Typography>
                                    </Tooltip>
                                    {deskripsi ? (
                                        <Tooltip title={deskripsi}>
                                            <Typography
                                                fontSize={12}
                                                color="#76747C"
                                            >
                                                {truncate(deskripsi, 30)}
                                            </Typography>
                                        </Tooltip>
                                    ) : undefined}
                                    <Typography
                                        mt={isPhoneScreen ? undefined : 1}
                                        fontSize={12}
                                        color={
                                            warehouseData?.isDropship === 1
                                                ? (stokPusat < 5 &&
                                                      stokPusat !== null) ||
                                                  !detailBundling.every(
                                                      (item) =>
                                                          item.stok === null ||
                                                          item.stok > 0,
                                                  )
                                                    ? "#ef4444"
                                                    : "#76747C"
                                                : (jumlahStok < 5 &&
                                                      jumlahStok !== null) ||
                                                  !detailBundling.every(
                                                      (item) =>
                                                          item.stok === null ||
                                                          item.stok > 0,
                                                  )
                                                ? "#ef4444"
                                                : "#76747C"
                                        }
                                        fontWeight={
                                            warehouseData?.isDropship === 1
                                                ? (stokPusat < 5 &&
                                                      stokPusat !== null) ||
                                                  !detailBundling.every(
                                                      (item) =>
                                                          item.stok === null ||
                                                          item.stok > 0,
                                                  )
                                                    ? 700
                                                    : 600
                                                : (jumlahStok < 5 &&
                                                      jumlahStok !== null) ||
                                                  !detailBundling.every(
                                                      (item) =>
                                                          item.stok === null ||
                                                          item.stok > 0,
                                                  )
                                                ? 700
                                                : 600
                                        }
                                    >
                                        {warehouseData?.isDropship === 1
                                            ? detailBundling.length > 0
                                                ? detailBundling.every(
                                                      (item) =>
                                                          item.stok === null ||
                                                          item.stok > 0,
                                                  )
                                                    ? "Otomatis"
                                                    : "Ada barang yang habis"
                                                : stokPusat === null
                                                ? "Tanpa stok"
                                                : stokPusat > 5
                                                ? "Tersedia " +
                                                  toRibuan(stokPusat) +
                                                  " barang"
                                                : stokPusat <= 5 &&
                                                  stokPusat > 0
                                                ? "Barang tersisa " +
                                                  toRibuan(stokPusat)
                                                : "Barang telah habis"
                                            : detailBundling.length > 0
                                            ? detailBundling.every(
                                                  (item) =>
                                                      item.stok === null ||
                                                      item.stok > 0,
                                              )
                                                ? "Otomatis"
                                                : "Ada barang yang habis"
                                            : jumlahStok === null
                                            ? "Tanpa stok"
                                            : jumlahStok > 5
                                            ? "Tersedia " +
                                              toRibuan(jumlahStok) +
                                              " barang"
                                            : jumlahStok <= 5 && jumlahStok > 0
                                            ? "Barang tersisa " +
                                              toRibuan(jumlahStok)
                                            : "Barang telah habis"}
                                    </Typography>
                                    {hargaMultiple.length > 0 ? (
                                        <Typography
                                            variant="subtitle2"
                                            color="#76747C"
                                            fontWeight="bold"
                                        >
                                            {toRupiah(
                                                Number(hargaMultiple[0].harga),
                                            )}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="subtitle2"
                                            color="#76747C"
                                            fontWeight="bold"
                                        >
                                            {toRupiah(harga)}
                                        </Typography>
                                    )}
                                </Link>
                            </Grid>
                        </Grid>
                    </Stack>
                    <Button
                        disabled={
                            warehouseData?.isDropship === 1
                                ? cart.findIndex((c) => c.id === id) >= 0 ||
                                  stokPusat === null ||
                                  (stokPusat <= 0 && stokPusat !== null) ||
                                  !detailBundling.every(
                                      (item) =>
                                          item.stok === null || item.stok > 0,
                                  )
                                : cart.findIndex((c) => c.id === id) >= 0 ||
                                  jumlahStok === null ||
                                  (jumlahStok <= 0 && jumlahStok !== null) ||
                                  !detailBundling.every(
                                      (item) =>
                                          item.stok === null || item.stok > 0,
                                  )
                        }
                        variant="contained"
                        onClick={() =>
                            addToCart({
                                id,
                                namaBarang,
                                namaKategori: kategori.nama,
                                harga:
                                    hargaMultiple.length > 0
                                        ? Number(hargaMultiple[0].harga)
                                        : harga,
                                url:
                                    multipleGambar && multipleGambar.length > 0
                                        ? multipleGambar[0].gambar
                                        : "",
                                qty: 1,
                                sku,
                                jumlahStok,
                                stokPusat,
                                detailBundling,
                                hpp,
                                faktur: [],
                                multipleHarga: hargaMultiple.map((li) => ({
                                    id: li.id,
                                    harga: Number(li.harga),
                                    keterangan: li.keterangan,
                                })),
                            })
                        }
                        startIcon={
                            cart.findIndex((c) => c.id === id) >= 0 ? (
                                <ShoppingCartIcon />
                            ) : (
                                <AddShoppingCartIcon />
                            )
                        }
                        sx={{
                            gridColumn: isPhoneScreen ? "1 / 3" : "unset",
                        }}
                    >
                        {warehouseData?.isDropship === 1
                            ? cart.findIndex((c) => c.id === id) >= 0
                                ? "Di Keranjang"
                                : (stokPusat <= 0 && stokPusat !== null) ||
                                  !detailBundling.every(
                                      (item) =>
                                          item.stok === null || item.stok > 0,
                                  )
                                ? "Stok Habis"
                                : "Pesan"
                            : cart.findIndex((c) => c.id === id) >= 0
                            ? "Di Keranjang"
                            : (jumlahStok <= 0 && jumlahStok !== null) ||
                              !detailBundling.every(
                                  (item) => item.stok === null || item.stok > 0,
                              )
                            ? "Stok Habis"
                            : "Pesan"}
                    </Button>
                </Box>
            </Card>
        </React.Fragment>
    );
};

export default CardBarang;
